import { useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import Database from '../../types/Database'
import {
  validateAccountName,
  validateBsb,
  validateAccountNumber
} from '../../utils/bankDetailsValidation'
import * as bankDetailsService from '../../services/bankDetailsService'

export interface BankDetailsFormProps {
  userId: string
  existingDetails?: Database.BankDetails | null
  onSaved?: () => void
  onRemoved?: () => void
}

const BankDetailsForm = ({
  userId,
  existingDetails,
  onSaved,
  onRemoved
}: BankDetailsFormProps) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<Database.BankDetails>({
    defaultValues: {
      accountName: existingDetails?.accountName ?? '',
      bsb: existingDetails?.bsb ?? '',
      accountNumber: existingDetails?.accountNumber ?? ''
    }
  })

  const onSubmit = async (data: Database.BankDetails) => {
    setSuccessMessage(null)
    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      await bankDetailsService.saveBankDetails(userId, data)
      setSuccessMessage('Bank details saved successfully.')
      onSaved?.()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save bank details. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemove = async () => {
    setSuccessMessage(null)
    setErrorMessage(null)
    setIsRemoving(true)

    try {
      await bankDetailsService.removeBankDetails(userId)
      setSuccessMessage('Bank details removed successfully.')
      onRemoved?.()
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to remove bank details. Please try again.'
      setErrorMessage(message)
    } finally {
      setIsRemoving(false)
    }
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant='h6' gutterBottom>
        Bank Details
      </Typography>

      {successMessage && (
        <Alert severity='success' sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      <TextField
        fullWidth
        label='Account Name'
        margin='normal'
        error={!!errors.accountName}
        helperText={errors.accountName?.message}
        {...register('accountName', {
          validate: value => validateAccountName(value) ?? undefined
        })}
      />

      <TextField
        fullWidth
        label='BSB'
        margin='normal'
        error={!!errors.bsb}
        helperText={errors.bsb?.message}
        {...register('bsb', {
          validate: value => validateBsb(value) ?? undefined
        })}
      />

      <TextField
        fullWidth
        label='Account Number'
        margin='normal'
        error={!!errors.accountNumber}
        helperText={errors.accountNumber?.message}
        {...register('accountNumber', {
          validate: value => validateAccountNumber(value) ?? undefined
        })}
      />

      <Button
        type='submit'
        variant='contained'
        fullWidth
        sx={{ mt: 2 }}
        disabled={isSubmitting || isRemoving}
        startIcon={isSubmitting ? <CircularProgress size={20} /> : undefined}
      >
        {isSubmitting ? 'Saving...' : 'Save Bank Details'}
      </Button>

      {existingDetails && (
        <Button
          variant='outlined'
          color='error'
          fullWidth
          sx={{ mt: 1 }}
          onClick={handleRemove}
          disabled={isSubmitting || isRemoving}
          startIcon={isRemoving ? <CircularProgress size={20} /> : undefined}
        >
          {isRemoving ? 'Removing...' : 'Remove Bank Details'}
        </Button>
      )}
    </Box>
  )
}

export default BankDetailsForm
